/**
 * Provider structured outputs — generic capture and derivation.
 *
 * A Vapi assistant can have any number of Structured Outputs attached, each
 * producing a result of an arbitrary JSON type (object, string, number, boolean)
 * under a UUID key. Which outputs exist differs per assistant and changes over
 * time as analysis plans are added and removed.
 *
 * The contract here is therefore: **store everything verbatim, derive nothing
 * destructively.** Parsing produces a flat list that is persisted as-is; the
 * derivation helpers below are a best-effort convenience layer that feeds the
 * portal's existing filterable columns. If a derivation misses an unfamiliar
 * output, nothing is lost — the raw result is still on the call.
 *
 * Nothing keys off a specific structured-output UUID, and no output name is
 * required to exist.
 */

export interface StructuredOutputRecord {
  /** The provider's structured-output id (the UUID key). */
  id: string;
  /** Human-readable name the provider assigned, e.g. "Customer Sentiment". */
  name: string;
  /** Arbitrary JSON. May be an object, string, number, boolean, or null. */
  result: unknown;
}

/**
 * Read `artifact.structuredOutputs` into a stable list.
 *
 * Accepts either the artifact object or the structuredOutputs map directly, so
 * callers can pass whichever they have. Entries without a usable result are
 * dropped; entries with an unnamed output fall back to their id so a row is
 * never silently discarded.
 */
export function parseStructuredOutputs(input: unknown): StructuredOutputRecord[] {
  const artifact = asRecord(input);
  if (!artifact) return [];

  // Tolerate being handed the artifact, the map, or a whole message.
  const map = asRecord(artifact.structuredOutputs)
    ?? asRecord(asRecord(artifact.artifact)?.structuredOutputs)
    ?? artifact;

  const outputs: StructuredOutputRecord[] = [];
  for (const [id, entry] of Object.entries(map)) {
    const record = asRecord(entry);
    if (!record || !('result' in record)) continue;

    const result = record.result;
    if (result === undefined) continue;

    outputs.push({
      id,
      name: typeof record.name === 'string' && record.name.trim() ? record.name.trim() : id,
      result,
    });
  }
  return outputs.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Project structured outputs into flat records the analysis alias-matcher can
 * read.
 *
 * Object results contribute their own keys (`lead_quality_score`, `call_type`,
 * …). Scalar results have no keys of their own, so they contribute a single
 * entry keyed by the slugified output name — "Customer Sentiment" becomes
 * `{ customer_sentiment: "positive" }`. That keeps the mapping driven by the
 * provider's own naming rather than by a hardcoded list.
 */
export function structuredOutputSources(
  outputs: StructuredOutputRecord[],
): Array<Record<string, unknown>> {
  const sources: Array<Record<string, unknown>> = [];
  for (const output of outputs) {
    const record = asRecord(output.result);
    if (record) {
      sources.push(record);
      continue;
    }
    if (output.result === null) continue;
    sources.push({ [slugify(output.name)]: output.result });
  }
  return sources;
}

/**
 * Best-effort free-text summary.
 *
 * Vapi's built-in summarizer can be disabled in favour of a summary structured
 * output, in which case `analysis.summary` arrives as an empty string. Falls
 * back to the longest string-valued output whose name mentions "summary", which
 * avoids picking a short label field over the real narrative.
 */
export function deriveSummary(outputs: StructuredOutputRecord[]): string | undefined {
  const candidates = outputs
    .filter((output) => typeof output.result === 'string'
      && output.result.trim().length > 0
      && slugify(output.name).includes('summary'))
    .map((output) => (output.result as string).trim())
    .sort((a, b) => b.length - a.length);
  return candidates[0];
}

/** Best-effort sentiment label (e.g. "positive"), when an output reports one. */
export function deriveSentiment(outputs: StructuredOutputRecord[]): string | undefined {
  for (const source of structuredOutputSources(outputs)) {
    for (const key of ['customer_sentiment', 'sentiment', 'caller_sentiment', 'call_sentiment']) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim().toLowerCase();
    }
  }
  return undefined;
}

/**
 * Best-effort numeric scores. `lead` is how promising the caller is; `assistant`
 * is how well the agent handled the call. Both are returned on the provider's
 * own scale (typically 1-10) rather than rescaled, so a change in scale does not
 * silently reinterpret history.
 */
export function deriveScores(outputs: StructuredOutputRecord[]): {
  lead?: number;
  assistant?: number;
} {
  const sources = structuredOutputSources(outputs);
  return {
    lead: pickNumber(sources, ['lead_quality_score', 'lead_score', 'leadScore', 'lead_rating']),
    assistant: pickNumber(sources, [
      'overall_score',
      'call_quality_score',
      'assistant_score',
      'performance_score',
    ]),
  };
}

/**
 * Best-effort caller identity from whatever a contact-information output reports.
 *
 * Analysis plans disagree on field naming (`caller_first_name` vs `first_name`
 * vs `name`), and for web calls there is no transport-level phone number at all,
 * so the only caller detail that exists is whatever the assistant collected
 * during the conversation. Without this, such calls display as "Unknown Caller"
 * even though the analysis captured a name and number.
 */
export function deriveCallerIdentity(outputs: StructuredOutputRecord[]): {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phone?: string;
  email?: string;
  company?: string;
} {
  const sources = structuredOutputSources(outputs);
  const firstName = pickString(sources, ['caller_first_name', 'first_name', 'firstName']);
  const lastName = pickString(sources, ['caller_last_name', 'last_name', 'lastName']);
  const explicitFull = pickString(sources, ['caller_name', 'caller_full_name', 'full_name', 'name']);
  const combined = [firstName, lastName].filter(Boolean).join(' ');

  return {
    firstName,
    lastName,
    fullName: explicitFull ?? (combined || undefined),
    phone: pickString(sources, [
      'caller_phone_number', 'caller_phone', 'phone_number', 'phone', 'callback_number',
    ]),
    email: pickString(sources, ['caller_email', 'email_address', 'email']),
    company: pickString(sources, [
      'caller_business_name', 'business_name', 'company_name', 'company', 'organization',
    ]),
  };
}

export function pickString(
  sources: Array<Record<string, unknown>>,
  keys: string[],
): string | undefined {
  for (const source of sources) {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
  }
  return undefined;
}

export function pickNumber(
  sources: Array<Record<string, unknown>>,
  keys: string[],
): number | undefined {
  for (const source of sources) {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
        return Number(value);
      }
    }
  }
  return undefined;
}

export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
